import { Box, Stack, useTheme } from "@mui/material";
import { Button, FoodsIcon, FoodsSelect, Typography } from "@foodpilot/foods";
import { useTranslation } from "react-i18next";

import { RemoteErrorAlert } from "src/components/PageState/RemoteErrorAlert";
import { ImpactScoreColumn } from "../Recipe/impactScoreColumn";
import { LineScoreValue } from "../Layout/LineScoreValue";
import { FoodsSelectTriggerAdapter } from "src/components/EntityForm/FoodsSelectTriggerAdapter";
import type {
    TransportMaterializationFailure,
    TransportOption,
    TransportSummary,
} from "./useProductTransportSelectorData";

type ProductTransportInlineSelectorViewProps = {
    alignScoreColumn: boolean;
    anchorIsSelectable: boolean;
    catalogueError: boolean;
    catalogueLoaded: boolean;
    catalogueLoading: boolean;
    disabled: boolean;
    effectiveSelectedOption: TransportOption | null;
    hasPersistedSelection: boolean;
    isManageMyTransportEnabled: boolean;
    label?: string;
    lineScore: string | null;
    materializationError: TransportMaterializationFailure | null;
    onOptionChange: (option: TransportOption | null) => void;
    onRetryCatalogue: () => void;
    onRetryMaterialization: (failure: TransportMaterializationFailure) => void | Promise<void>;
    options: TransportOption[];
    persistedTransportLabel: string | null;
    resolvedTransportLabel?: string;
    routeFieldMinHeight: string;
    routePickerLayout: boolean;
    scoreOnly: boolean;
    scoreUnit: string;
    selectedOption: TransportOption | null;
    summary: TransportSummary;
};

export const ProductTransportInlineSelectorView = (props: ProductTransportInlineSelectorViewProps) => {
    const {
        alignScoreColumn,
        anchorIsSelectable,
        catalogueError,
        catalogueLoaded,
        catalogueLoading,
        disabled,
        effectiveSelectedOption,
        hasPersistedSelection,
        isManageMyTransportEnabled,
        label,
        lineScore,
        materializationError,
        onOptionChange,
        onRetryCatalogue,
        onRetryMaterialization,
        options,
        persistedTransportLabel,
        resolvedTransportLabel,
        routeFieldMinHeight,
        routePickerLayout,
        scoreOnly,
        scoreUnit,
        selectedOption,
        summary,
    } = props;
    const theme = useTheme();
    const { t } = useTranslation();
    const hasUnmatchedPersistedSelection = hasPersistedSelection && selectedOption === null;
    const noRouteLabel = t("transport.no_selected_route", "No route");
    const showRouteSelector =
        materializationError !== null ||
        (!hasUnmatchedPersistedSelection &&
            (options.length > 1 || (isManageMyTransportEnabled && options.length === 1)));

    const routeSelector =
        showRouteSelector ?
            <FoodsSelectTriggerAdapter accessibleLabel={label ?? t("transport.select_route", "Select a route")}>
                <FoodsSelect<TransportOption>
                    disabled={disabled || !anchorIsSelectable}
                    options={options}
                    selectedOption={effectiveSelectedOption}
                    getId={(option) => `${option.kind}:${option.id}`}
                    getName={(option) => option.label}
                    onChange={onOptionChange}
                    closeOnSelect
                    noValue={noRouteLabel}
                    textOptions={{
                        placeholder:
                            persistedTransportLabel ??
                            (routePickerLayout ?
                                t("transport.select_route", "Select a route")
                            :   (resolvedTransportLabel ?? t("transport.reference_circuit", "Reference route"))),
                        searchPlaceholder: t("component.select.searchplaceholder", "Search…"),
                        emptyOptionsText: t("component.select.nooptions", "No option available"),
                        noResult: t("component.select.noresultfound", "No results found"),
                    }}
                    BoxProps={{ minWidth: 0, width: "100%" }}
                    BoxOptionsProps={{ minWidth: theme.spacing(35), width: "100%" }}
                />
            </FoodsSelectTriggerAdapter>
        :   <Box
                sx={{
                    minHeight: routeFieldMinHeight,
                    display: "flex",
                    alignItems: "center",
                    color: theme.custom.grey[2800],
                    minWidth: 0,
                    overflow: "hidden",
                }}
            >
                <Typography variant="body-regular" noWrap sx={{ minWidth: 0 }}>
                    {effectiveSelectedOption?.label ??
                        persistedTransportLabel ??
                        (!hasPersistedSelection ? resolvedTransportLabel : null) ??
                        t("transport.no_available_route", "No transport route available")}
                </Typography>
            </Box>;

    const materializationAlert =
        materializationError === null ? null : (
            <RemoteErrorAlert
                status={materializationError.failureKind === "contract" ? "contract-error" : "server-error"}
                retryLabel={t("common.retry", "Retry")}
                onRetry={() => onRetryMaterialization(materializationError)}
                message={
                    materializationError.failureKind === "contract" ?
                        t(
                            "transport.circuits.contract_error_after_save",
                            "The route was saved, but its response is invalid. Try again.",
                        )
                    : materializationError.failureKind === "refresh" ?
                        t(
                            "transport.circuits.refresh_error",
                            "Unable to refresh transport itinerary. Showing saved data.",
                        )
                    :   t("transport.circuits.save_error", "The transport route could not be saved. Please try again.")
                }
            />
        );

    const abandonMaterializationAction =
        materializationError === null ? null : (
            <Button type="button" variant="text" onClick={() => onOptionChange(null)} sx={{ alignSelf: "flex-start" }}>
                {noRouteLabel}
            </Button>
        );

    const catalogueAlert =
        catalogueError ?
            <RemoteErrorAlert
                status="server-error"
                onRetry={onRetryCatalogue}
                message={t("transport.circuits.load_error", "Unable to load transport itinerary")}
            />
        : hasPersistedSelection && selectedOption === null && catalogueLoaded && !catalogueLoading ?
            <RemoteErrorAlert
                status="not-found"
                message={t("transport.circuits.not_found", "Transport itinerary not found")}
            />
        :   null;

    if (routePickerLayout) {
        return (
            <Stack data-testid="transport-route-picker" gap={theme.spacing(1)} sx={{ minWidth: 0, width: "100%" }}>
                {label && (
                    <Typography variant="body-semibold" sx={{ color: theme.custom.grey[2800] }}>
                        {label}
                    </Typography>
                )}

                <Stack
                    data-testid="transport-route-picker-controls"
                    direction="row"
                    alignItems="center"
                    gap={theme.spacing(2)}
                    sx={{ minWidth: 0, width: "100%" }}
                >
                    <Box sx={{ flex: 1, minWidth: 0 }}>{routeSelector}</Box>
                    <Box
                        data-testid="transport-route-picker-score"
                        sx={{
                            alignItems: "center",
                            display: "flex",
                            flexShrink: 0,
                            height: routeFieldMinHeight,
                            justifyContent: "flex-end",
                            width: theme.custom.scoreValue.inline.width,
                        }}
                    >
                        <LineScoreValue value={lineScore} unit={scoreUnit} />
                    </Box>
                </Stack>

                {materializationAlert}
                {abandonMaterializationAction}
                {catalogueAlert}
            </Stack>
        );
    }

    return (
        <Stack gap={theme.spacing(0.75)} sx={{ minWidth: 0 }}>
            {!scoreOnly && label && (
                <Typography variant="small-medium" sx={{ color: theme.custom.grey[2800] }}>
                    {label}
                </Typography>
            )}
            {(!scoreOnly || materializationError !== null) && routeSelector}
            {materializationAlert}
            {abandonMaterializationAction}
            {catalogueAlert}
            <TransportInlineSummary
                summary={summary}
                lineScore={lineScore}
                scoreUnit={scoreUnit}
                alignScoreColumn={alignScoreColumn}
                scoreOnly={scoreOnly}
            />
        </Stack>
    );
};

const TransportInlineSummary = ({
    summary,
    lineScore,
    scoreUnit,
    alignScoreColumn = false,
    scoreOnly = false,
}: {
    summary: TransportSummary;
    lineScore: string | null;
    scoreUnit: string;
    alignScoreColumn?: boolean;
    scoreOnly?: boolean;
}) => {
    const theme = useTheme();
    const { t } = useTranslation();

    if (alignScoreColumn) {
        return (
            <Stack direction="row" alignItems="center" justifyContent="space-between" width="100%">
                <Stack
                    direction="row"
                    alignItems="center"
                    gap={theme.spacing(1)}
                    flex={1}
                    minWidth={0}
                    flexWrap="wrap"
                    useFlexGap
                >
                    {scoreOnly && (
                        <Typography
                            variant="small-medium"
                            sx={{ color: theme.custom.grey[2800], whiteSpace: "nowrap" }}
                        >
                            {t("product.v2.recipe.transport_impact", "Transport impact:")}
                        </Typography>
                    )}
                    <TransportModeIcons summary={summary} compact={scoreOnly} />
                    <TransportDistance summary={summary} emphasized={scoreOnly} nowrap />
                </Stack>
                <ImpactScoreColumn value={lineScore} unit={scoreUnit} />
            </Stack>
        );
    }

    return (
        <Stack
            direction="row"
            alignItems="center"
            justifyContent="flex-end"
            gap={theme.spacing(1)}
            flexWrap="wrap"
            useFlexGap
        >
            <TransportModeIcons summary={summary} />
            <TransportDistance summary={summary} />
            <LineScoreValue value={lineScore} unit={scoreUnit} />
        </Stack>
    );
};

const TransportModeIcons = ({ summary, compact = false }: { summary: TransportSummary; compact?: boolean }) => {
    const theme = useTheme();
    const { t } = useTranslation();

    return (
        <Stack direction="row" alignItems="center" gap={theme.spacing(0.5)}>
            {summary.modeIcons.length > 0 ?
                summary.modeIcons.map((modeIcon) => (
                    <Box
                        key={modeIcon.key}
                        component="span"
                        title={modeIcon.label}
                        sx={{
                            width: theme.spacing(compact ? 2 : 3),
                            height: theme.spacing(compact ? 2 : 3),
                            borderRadius: compact ? 0 : theme.spacing(0.5),
                            display: "grid",
                            placeItems: "center",
                            ...(compact ?
                                { color: theme.custom.grey[2500] }
                            :   {
                                    color: theme.custom.grey[100],
                                    background: theme.custom.grey.gradientDark,
                                }),
                            "& svg path": {
                                fill: "currentColor",
                            },
                        }}
                    >
                        <FoodsIcon icon={modeIcon.icon} size={2} />
                    </Box>
                ))
            :   <Typography variant="small-regular" sx={{ color: theme.custom.grey[2000] }}>
                    {t("transport.mode_unknown", "Mode unavailable")}
                </Typography>
            }
        </Stack>
    );
};

const TransportDistance = ({
    summary,
    emphasized = false,
    nowrap = false,
}: {
    summary: TransportSummary;
    emphasized?: boolean;
    nowrap?: boolean;
}) => {
    const theme = useTheme();
    const { t } = useTranslation();

    return (
        <Typography
            variant={emphasized ? "small-semibold" : "small-regular"}
            sx={{
                color: emphasized ? theme.custom.grey[2800] : theme.custom.grey[2000],
                whiteSpace: nowrap ? "nowrap" : undefined,
            }}
        >
            {summary.distanceKm != null ?
                t("transport.distance_km", "{{distance}} km", { distance: Math.round(summary.distanceKm) })
            :   t("transport.meta.distance_unknown", "Distance unavailable")}
        </Typography>
    );
};
